// SPDX-License-Identifier: GPL-3.0-or-later
// Music voices: drum, bass, pad and lead sounds made with WebAudio nodes. No sound files.
//
// Each voice makes a small graph, starts it at a context time and registers it. A graph is
// disconnected when its source ends, or when `prune` finds that its end time has passed.
// The kit refuses a new voice when the live count is at the cap. This bounds the node count in a
// long session. One noise buffer serves all noise voices.

import { type LeadWave, midiToHz } from "./music-patterns.ts";

/** Most voices that play at one time. */
export const MAX_VOICES = 28;
/** Extra voices that only a stinger may use. */
export const STINGER_HEADROOM = 8;

const SILENCE = 0.0001;
const NOISE_SECONDS = 1;

interface Voice {
  end: number;
  nodes: AudioNode[];
  done: boolean;
}

/** The voices of the music. Every `t` is a context time in seconds. */
export interface VoiceKit {
  kick(t: number, vel: number, priority?: boolean): void;
  snare(t: number, vel: number, priority?: boolean): void;
  hat(t: number, vel: number, open: boolean, seed: number, priority?: boolean): void;
  bass(t: number, midi: number, dur: number, vel: number): void;
  pad(t: number, m1: number, m2: number, m3: number, dur: number, vel: number): void;
  lead(t: number, midi: number, dur: number, vel: number, wave: LeadWave, priority?: boolean): void;
  /** Disconnect the voices that have ended. */
  prune(now: number): void;
  /** The number of voices that are live now. */
  liveCount(): number;
  /** Disconnect every voice. */
  dispose(): void;
}

/** Fill one noise buffer with a fixed pseudo-random pattern (no Math.random). */
function makeNoise(ctx: BaseAudioContext): AudioBuffer {
  const length = Math.max(1, Math.floor(ctx.sampleRate * NOISE_SECONDS));
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  let seed = 22695477;
  for (let i = 0; i < length; i++) {
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
    data[i] = seed / 2147483648 - 1;
  }
  return buffer;
}

function disconnectVoice(v: Voice): void {
  if (v.done) return;
  v.done = true;
  for (const node of v.nodes) {
    try {
      node.disconnect();
    } catch {
      // A node that is already free needs no action.
    }
  }
}

/** A gain envelope: rise to `peak`, then fall to silence at `total` seconds after `t`. */
function envelope(g: GainNode, t: number, peak: number, attack: number, total: number): void {
  g.gain.setValueAtTime(SILENCE, t);
  g.gain.linearRampToValueAtTime(Math.max(SILENCE, peak), t + attack);
  g.gain.exponentialRampToValueAtTime(SILENCE, t + total);
}

/** The state that every voice of one kit shares. */
interface KitState {
  readonly ctx: BaseAudioContext;
  readonly dest: AudioNode;
  readonly noise: AudioBuffer;
  readonly live: Voice[];
}

function voiceKitAllowed(k: KitState, vel: number, priority: boolean): boolean {
  return vel > 0 && k.live.length < MAX_VOICES + (priority ? STINGER_HEADROOM : 0);
}

function voiceKitRegister(
  k: KitState,
  end: number,
  nodes: AudioNode[],
  src: AudioScheduledSourceNode,
): void {
  const voice: Voice = { end, nodes, done: false };
  src.onended = () => disconnectVoice(voice);
  k.live.push(voice);
}

function voiceKitNoiseSource(
  k: KitState,
  t: number,
  seed: number,
  dur: number,
): AudioBufferSourceNode {
  const src = k.ctx.createBufferSource();
  src.buffer = k.noise;
  src.start(t, (Math.abs(seed) * 0.137) % (NOISE_SECONDS * 0.8));
  src.stop(t + dur);
  return src;
}

function voiceKitKick(k: KitState, t: number, vel: number, priority: boolean): void {
  if (!voiceKitAllowed(k, vel, priority)) return;
  const o = k.ctx.createOscillator();
  const g = k.ctx.createGain();
  o.type = "sine";
  o.frequency.setValueAtTime(130, t);
  o.frequency.exponentialRampToValueAtTime(42, t + 0.12);
  g.gain.setValueAtTime(0.9 * vel, t);
  g.gain.exponentialRampToValueAtTime(SILENCE, t + 0.28);
  o.connect(g);
  g.connect(k.dest);
  o.start(t);
  o.stop(t + 0.3);
  voiceKitRegister(k, t + 0.3, [o, g], o);
}

function voiceKitSnare(k: KitState, t: number, vel: number, priority: boolean): void {
  if (!voiceKitAllowed(k, vel, priority)) return;
  const dur = 0.2;
  const src = voiceKitNoiseSource(k, t, 3, dur);
  const f = k.ctx.createBiquadFilter();
  const o = k.ctx.createOscillator();
  const g = k.ctx.createGain();
  f.type = "bandpass";
  f.frequency.value = 1900;
  o.type = "triangle";
  o.frequency.value = 185;
  envelope(g, t, 0.45 * vel, 0.002, dur - 0.02);
  src.connect(f);
  f.connect(g);
  o.connect(g);
  g.connect(k.dest);
  o.start(t);
  o.stop(t + dur);
  voiceKitRegister(k, t + dur, [src, f, o, g], src);
}

function voiceKitHat(
  k: KitState,
  t: number,
  vel: number,
  open: boolean,
  seed: number,
  priority: boolean,
): void {
  if (!voiceKitAllowed(k, vel, priority)) return;
  const dur = open ? 0.24 : 0.06;
  const src = voiceKitNoiseSource(k, t, seed, dur);
  const f = k.ctx.createBiquadFilter();
  const g = k.ctx.createGain();
  f.type = "highpass";
  f.frequency.value = 7500;
  envelope(g, t, 0.14 * vel, 0.001, dur - 0.005);
  src.connect(f);
  f.connect(g);
  g.connect(k.dest);
  voiceKitRegister(k, t + dur, [src, f, g], src);
}

function voiceKitBass(k: KitState, t: number, midi: number, dur: number, vel: number): void {
  if (!voiceKitAllowed(k, vel, false)) return;
  const o = k.ctx.createOscillator();
  const f = k.ctx.createBiquadFilter();
  const g = k.ctx.createGain();
  o.type = "sawtooth";
  o.frequency.value = midiToHz(midi);
  f.type = "lowpass";
  f.frequency.setValueAtTime(950, t);
  f.frequency.exponentialRampToValueAtTime(280, t + dur);
  envelope(g, t, 0.36 * vel, 0.01, dur + 0.06);
  o.connect(f);
  f.connect(g);
  g.connect(k.dest);
  o.start(t);
  o.stop(t + dur + 0.08);
  voiceKitRegister(k, t + dur + 0.08, [o, f, g], o);
}

function voiceKitPad(
  k: KitState,
  t: number,
  m1: number,
  m2: number,
  m3: number,
  dur: number,
  vel: number,
): void {
  if (!voiceKitAllowed(k, vel, false)) return;
  const f = k.ctx.createBiquadFilter();
  const g = k.ctx.createGain();
  f.type = "lowpass";
  f.frequency.value = 1400;
  envelope(g, t, 0.09 * vel, 0.3, dur);
  f.connect(g);
  g.connect(k.dest);
  const nodes: AudioNode[] = [f, g];
  let first: AudioScheduledSourceNode | null = null;
  for (const [i, midi] of [m1, m2, m3].entries()) {
    const o = k.ctx.createOscillator();
    o.type = "triangle";
    o.frequency.value = midiToHz(midi);
    o.detune.value = (i - 1) * 7;
    o.connect(f);
    o.start(t);
    o.stop(t + dur + 0.02);
    nodes.push(o);
    first ??= o;
  }
  if (first) voiceKitRegister(k, t + dur + 0.02, nodes, first);
}

function voiceKitLead(
  k: KitState,
  t: number,
  midi: number,
  dur: number,
  vel: number,
  wave: LeadWave,
  priority: boolean,
): void {
  if (!voiceKitAllowed(k, vel, priority)) return;
  const o = k.ctx.createOscillator();
  const f = k.ctx.createBiquadFilter();
  const g = k.ctx.createGain();
  o.type = wave;
  o.frequency.value = midiToHz(midi);
  f.type = "lowpass";
  f.frequency.value = 3200;
  envelope(g, t, 0.15 * vel, 0.005, dur + 0.04);
  o.connect(f);
  f.connect(g);
  g.connect(k.dest);
  o.start(t);
  o.stop(t + dur + 0.05);
  voiceKitRegister(k, t + dur + 0.05, [o, f, g], o);
}

function voiceKitPrune(k: KitState, now: number): void {
  const live = k.live;
  let write = 0;
  for (let read = 0; read < live.length; read++) {
    const voice = live[read];
    if (!voice) continue;
    if (voice.done || voice.end <= now) {
      disconnectVoice(voice);
    } else {
      live[write++] = voice;
    }
  }
  live.length = write;
}

function voiceKitDispose(k: KitState): void {
  for (const voice of k.live) disconnectVoice(voice);
  k.live.length = 0;
}

/** Make the voice kit for one context. All voices connect to `dest`. */
export function createVoiceKit(ctx: BaseAudioContext, dest: AudioNode): VoiceKit {
  const k: KitState = { ctx, dest, noise: makeNoise(ctx), live: [] };
  return {
    kick: (t, vel, priority = false) => voiceKitKick(k, t, vel, priority),
    snare: (t, vel, priority = false) => voiceKitSnare(k, t, vel, priority),
    hat: (t, vel, open, seed, priority = false) => voiceKitHat(k, t, vel, open, seed, priority),
    bass: (t, midi, dur, vel) => voiceKitBass(k, t, midi, dur, vel),
    pad: (t, m1, m2, m3, dur, vel) => voiceKitPad(k, t, m1, m2, m3, dur, vel),
    lead: (t, midi, dur, vel, wave, priority = false) =>
      voiceKitLead(k, t, midi, dur, vel, wave, priority),
    prune: (now) => voiceKitPrune(k, now),
    liveCount: () => k.live.length,
    dispose: () => voiceKitDispose(k),
  };
}
